/**
 * PWA Update Prompt Component
 * Shows a notification when a new version of the app is available
 */

import { useState, useEffect } from 'react';
import { RefreshCw, X, Download } from 'lucide-react';

export function PWAUpdatePrompt() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [updateSW, setUpdateSW] = useState<(() => Promise<void>) | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Listen for update available event from main.tsx
    const handleUpdateAvailable = (event: CustomEvent) => {
      console.log('[PWAUpdatePrompt] Update available');
      setShowUpdatePrompt(true);
      if (event.detail?.updateSW) {
        setUpdateSW(() => event.detail.updateSW);
      }
    };

    window.addEventListener('pwa-update-available', handleUpdateAvailable as EventListener);

    // Check if updateSW is already available globally
    if ((window as any).__PWA_UPDATE_SW__) {
      // Don't show immediately - only show when there's actually an update
    }

    return () => {
      window.removeEventListener('pwa-update-available', handleUpdateAvailable as EventListener);
    };
  }, []);

  const handleUpdate = async () => {
    if (!updateSW) {
      // Fallback: reload the page
      window.location.reload();
      return;
    }

    setIsUpdating(true);
    try {
      await updateSW();
      // The page will reload automatically after update
    } catch (error) {
      console.error('[PWAUpdatePrompt] Update failed:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setShowUpdatePrompt(false);
  };

  if (!showUpdatePrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Download className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">
              Update Available
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              A new version of SAUBHAGYA Gaushala is available.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUpdating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Update Now
                  </>
                )}
              </button>
              <button
                onClick={handleDismiss}
                className="inline-flex items-center px-3 py-1.5 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors"
              >
                Later
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PWAUpdatePrompt;
